/**
 * ImageBlock.tsx
 *
 * A configurable image component for the no-code builder.
 *
 * Builder mode  → shows URL input + style controls (alignment, width, border-radius)
 * Journey mode  → renders the image with the configured styles
 *
 * Value shape:
 *   { src?: string; alt?: string; width?: string; align?: 'left'|'center'|'right'; borderRadius?: string; }
 */

import React, { useState } from 'react';
import { Image as ImageIcon } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ImageBlockValue {
  src?: string;
  alt?: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  borderRadius?: string;
}

interface ImageBlockProps {
  value?: ImageBlockValue;
  onChange?: (val: ImageBlockValue) => void;
  disabled?: boolean;
  isBuilderMode?: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const WIDTH_OPTIONS = [
  { label: '25%', value: '25%' },
  { label: '50%', value: '50%' },
  { label: '75%', value: '75%' },
  { label: '100%', value: '100%' },
];

const RADIUS_OPTIONS = [
  { label: 'None', value: '0px' },
  { label: 'Small', value: '8px' },
  { label: 'Medium', value: '16px' },
  { label: 'Large', value: '24px' },
  { label: 'Full', value: '9999px' },
];

const ALIGN_OPTIONS: { label: string; value: 'left' | 'center' | 'right'; icon: React.ReactNode }[] = [
  {
    label: 'Left',
    value: 'left',
    icon: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <line x1="1" y1="3" x2="13" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="1" y1="7" x2="9" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="1" y1="11" x2="11" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    label: 'Center',
    value: 'center',
    icon: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <line x1="1" y1="3" x2="13" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="3" y1="7" x2="11" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="2" y1="11" x2="12" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    label: 'Right',
    value: 'right',
    icon: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <line x1="1" y1="3" x2="13" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="5" y1="7" x2="13" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="3" y1="11" x2="13" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

const ImageBlock: React.FC<ImageBlockProps> = ({
  value = {},
  onChange,
  disabled = false,
  isBuilderMode = false,
}) => {
  const src = value.src ?? '';
  const alt = value.alt ?? '';
  const width = value.width ?? '100%';
  const align = value.align ?? 'center';
  const borderRadius = value.borderRadius ?? '0px';

  const [imgError, setImgError] = useState(false);

  const update = (patch: Partial<ImageBlockValue>) => {
    onChange?.({ ...value, ...patch });
  };

  // ── Journey mode: render image ──────────────────────────────────────────────
  if (!isBuilderMode) {
    if (!src) return null;
    return (
      <div
        className="w-full py-1"
        style={{ textAlign: align }}
      >
        <img
          src={src}
          alt={alt || 'Image'}
          style={{ width, borderRadius, display: 'inline-block', maxWidth: '100%' }}
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  // ── Builder mode: controls ──────────────────────────────────────────────────
  return (
    <div className="space-y-3 p-1">
      {/* Image URL */}
      <div className="space-y-1">
        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide select-none">
          Image URL
        </label>
        <input
          type="url"
          value={src}
          onChange={(e) => { setImgError(false); update({ src: e.target.value }); }}
          disabled={disabled}
          placeholder="https://example.com/image.png"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800
                     focus:outline-none focus:ring-2 focus:ring-intellend-green
                     disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
        />
      </div>

      {/* Alt text */}
      <div className="space-y-1">
        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide select-none">
          Alt Text
        </label>
        <input
          type="text"
          value={alt}
          onChange={(e) => update({ alt: e.target.value })}
          disabled={disabled}
          placeholder="Describe the image…"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800
                     focus:outline-none focus:ring-2 focus:ring-intellend-green
                     disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
        />
      </div>

      {/* Width + Alignment row */}
      <div className="flex gap-3">
        {/* Width */}
        <div className="flex-1 space-y-1">
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide select-none">
            Width
          </label>
          <div className="flex gap-1 flex-wrap">
            {WIDTH_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => update({ width: opt.value })}
                disabled={disabled}
                className={[
                  'px-2 py-1 text-xs rounded border transition-colors',
                  width === opt.value
                    ? 'bg-gray-800 text-white border-gray-800'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400',
                ].join(' ')}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Alignment */}
        <div className="space-y-1">
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide select-none">
            Align
          </label>
          <div className="flex gap-1">
            {ALIGN_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                title={opt.label}
                onClick={() => update({ align: opt.value })}
                disabled={disabled}
                className={[
                  'inline-flex items-center justify-center w-7 h-7 rounded border transition-colors',
                  align === opt.value
                    ? 'bg-gray-800 text-white border-gray-800'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400',
                ].join(' ')}
              >
                {opt.icon}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Border Radius */}
      <div className="space-y-1">
        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide select-none">
          Corner Radius
        </label>
        <div className="flex gap-1 flex-wrap">
          {RADIUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => update({ borderRadius: opt.value })}
              disabled={disabled}
              className={[
                'px-2 py-1 text-xs rounded border transition-colors',
                borderRadius === opt.value
                  ? 'bg-gray-800 text-white border-gray-800'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400',
              ].join(' ')}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div className="space-y-1">
        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide select-none">
          Preview
        </label>
        <div
          className="w-full min-h-[80px] border border-dashed border-gray-200 rounded-lg bg-gray-50 flex items-center overflow-hidden"
          style={{ justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center', padding: '8px' }}
        >
          {src && !imgError ? (
            <img
              src={src}
              alt={alt || 'Preview'}
              style={{ width, borderRadius, maxWidth: '100%', display: 'block' }}
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="flex flex-col items-center gap-1 text-gray-300 w-full py-4">
              <ImageIcon className="h-8 w-8" />
              <span className="text-xs">{imgError ? 'Failed to load image' : 'Enter a URL above to preview'}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageBlock;
